import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { SameTeamBadge } from "@/components/ui/same-team-badge";
import {
  ArrowLeft,
  CalendarDays,
  ClipboardPlus,
  FileText,
  Loader2,
  Pill,
  Plus,
  Stethoscope,
  Trash2,
  User,
  X,
  Copy,
  Check,
  AlertTriangle,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type ConsultationType = "in-person" | "phone" | "video";
type DaScoreTool = "das28-crp" | "das28-esr" | "cdai" | "sdai";
type ChangeType = "continued" | "changed" | "stopped" | "new";

interface MedicationReviewItem {
  medicationName: string;
  dose?: string | null;
  changeType: ChangeType;
  changeDetails?: string | null;
}

interface ConsultationRecord {
  id: number;
  patientId: number;
  practiceId: number | null;
  clinicianUserId: string;
  clinicianName: string | null;
  clinicianRole: string;
  clinicianTeamCode?: string | null;
  consultationDate: string;
  consultationType: ConsultationType;
  presentingComplaint: string | null;
  examinationFindings: string | null;
  diseaseActivityScore: string | null;
  diseaseActivityTool: DaScoreTool | null;
  medicationsReviewed: MedicationReviewItem[];
  planFreeText: string | null;
  nextReviewDate: string | null;
  gpCopyRequested: boolean;
  aiAssisted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PatientMedication {
  id: number;
  medicationName: string;
  dose: string | null;
  isActive: boolean;
}

interface PatientDetail {
  id: number;
  firstName: string;
  lastName: string;
  hospitalNumber: string;
  dateOfBirth: string;
  diagnosis: string | null;
  notes: string | null;
  medications: PatientMedication[];
}

interface UserProfile {
  role: "nurse" | "doctor" | "patient" | "pharmacist" | "physio" | "ot";
  teamCode?: string | null;
}

interface GpLetter {
  subject: string;
  body: string;
  generatedAt: string;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/London",
  });
}

function formatDate(d: string): string {
  if (!d) return "";
  const date = d.includes("T") ? new Date(d) : new Date(d + "T00:00:00");
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function nowDateTimeLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

const TOOL_LABEL: Record<DaScoreTool, string> = {
  "das28-crp": "DAS28-CRP",
  "das28-esr": "DAS28-ESR",
  cdai: "CDAI",
  sdai: "SDAI",
};

const CONSULTATION_TYPE_LABEL: Record<ConsultationType, string> = {
  "in-person": "In person",
  phone: "Phone",
  video: "Video",
};

function das28(tjc: number, sjc: number, marker: number, gh: number, tool: "crp" | "esr"): number {
  if (tool === "crp") {
    return 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.36 * Math.log(marker + 1) + 0.014 * gh + 0.96;
  }
  return 0.56 * Math.sqrt(tjc) + 0.28 * Math.sqrt(sjc) + 0.7 * Math.log(Math.max(marker, 1)) + 0.014 * gh;
}

function ScoreCalculator({
  tool,
  onApply,
}: {
  tool: DaScoreTool;
  onApply: (score: number) => void;
}) {
  const [tjc, setTjc] = useState(0);
  const [sjc, setSjc] = useState(0);
  const [marker, setMarker] = useState(tool === "das28-esr" ? 10 : 5);
  const [gh, setGh] = useState(50);
  const [cdai, setCdai] = useState({ tjc: 0, sjc: 0, pga: 0, ega: 0 });
  const [sdai, setSdai] = useState({ tjc: 0, sjc: 0, pga: 0, ega: 0, crp: 0 });

  if (tool === "cdai") {
    const score = cdai.tjc + cdai.sjc + cdai.pga + cdai.ega;
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
        <p className="text-xs font-medium text-slate-700">Calculate CDAI</p>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="TJC28" value={cdai.tjc} onChange={(v) => setCdai({ ...cdai, tjc: v })} max={28} />
          <NumberField label="SJC28" value={cdai.sjc} onChange={(v) => setCdai({ ...cdai, sjc: v })} max={28} />
          <NumberField label="Patient GA (0–10)" value={cdai.pga} onChange={(v) => setCdai({ ...cdai, pga: v })} max={10} step={0.1} />
          <NumberField label="Evaluator GA (0–10)" value={cdai.ega} onChange={(v) => setCdai({ ...cdai, ega: v })} max={10} step={0.1} />
        </div>
        <ApplyRow score={score} onApply={onApply} />
      </div>
    );
  }

  if (tool === "sdai") {
    const score = sdai.tjc + sdai.sjc + sdai.pga + sdai.ega + sdai.crp;
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
        <p className="text-xs font-medium text-slate-700">Calculate SDAI</p>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="TJC28" value={sdai.tjc} onChange={(v) => setSdai({ ...sdai, tjc: v })} max={28} />
          <NumberField label="SJC28" value={sdai.sjc} onChange={(v) => setSdai({ ...sdai, sjc: v })} max={28} />
          <NumberField label="Patient GA (0–10)" value={sdai.pga} onChange={(v) => setSdai({ ...sdai, pga: v })} max={10} step={0.1} />
          <NumberField label="Evaluator GA (0–10)" value={sdai.ega} onChange={(v) => setSdai({ ...sdai, ega: v })} max={10} step={0.1} />
          <NumberField label="CRP (mg/dL)" value={sdai.crp} onChange={(v) => setSdai({ ...sdai, crp: v })} step={0.1} />
        </div>
        <ApplyRow score={score} onApply={onApply} />
      </div>
    );
  }

  const score = das28(tjc, sjc, marker, gh, tool === "das28-crp" ? "crp" : "esr");
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-xs font-medium text-slate-700">Calculate {TOOL_LABEL[tool]}</p>
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="TJC28" value={tjc} onChange={setTjc} max={28} />
        <NumberField label="SJC28" value={sjc} onChange={setSjc} max={28} />
        <NumberField
          label={tool === "das28-crp" ? "CRP (mg/L)" : "ESR (mm/hr)"}
          value={marker}
          onChange={setMarker}
          step={tool === "das28-crp" ? 0.1 : 1}
        />
        <NumberField label="Global Health (0–100)" value={gh} onChange={setGh} max={100} />
      </div>
      <ApplyRow score={score} onApply={onApply} />
    </div>
  );
}

function ApplyRow({ score, onApply }: { score: number; onApply: (s: number) => void }) {
  const safe = Number.isFinite(score) ? score : 0;
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-700">
        Calculated: <strong className="text-slate-900">{safe.toFixed(2)}</strong>
      </span>
      <Button type="button" size="sm" variant="outline" onClick={() => onApply(Number(safe.toFixed(2)))}>
        Use this score
      </Button>
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  max,
  step = 1,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  max?: number;
  step?: number;
}) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="number"
        min={0}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );
}

function MedReviewRow({
  item,
  onChange,
  onRemove,
}: {
  item: MedicationReviewItem;
  onChange: (next: MedicationReviewItem) => void;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-white space-y-2">
      <div className="flex items-start gap-2">
        <Input
          value={item.medicationName}
          onChange={(e) => onChange({ ...item, medicationName: e.target.value })}
          placeholder="Medication name"
          className="text-sm"
        />
        <Input
          value={item.dose ?? ""}
          onChange={(e) => onChange({ ...item, dose: e.target.value })}
          placeholder="Dose"
          className="text-sm w-32"
        />
        <select
          value={item.changeType}
          onChange={(e) => onChange({ ...item, changeType: e.target.value as ChangeType })}
          className="border border-slate-200 rounded-lg px-2 py-2 text-sm bg-white"
        >
          <option value="continued">Continued</option>
          <option value="changed">Changed</option>
          <option value="stopped">Stopped</option>
          <option value="new">New</option>
        </select>
        <button
          type="button"
          onClick={onRemove}
          className="text-slate-400 hover:text-red-500 p-2"
          aria-label="Remove medication"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <Input
        value={item.changeDetails ?? ""}
        onChange={(e) => onChange({ ...item, changeDetails: e.target.value })}
        placeholder="Change details (e.g. dose increased, side effect)"
        className="text-sm"
      />
    </div>
  );
}

function ConsultationFormModal({
  patient,
  onClose,
  onCreated,
}: {
  patient: PatientDetail;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [consultationDate, setConsultationDate] = useState<string>(nowDateTimeLocal());
  const [consultationType, setConsultationType] = useState<ConsultationType>("in-person");
  const [presentingComplaint, setPresentingComplaint] = useState("");
  const [examinationFindings, setExaminationFindings] = useState("");
  const [diseaseActivityTool, setDiseaseActivityTool] = useState<DaScoreTool | "">("");
  const [diseaseActivityScore, setDiseaseActivityScore] = useState<string>("");
  const [showCalc, setShowCalc] = useState(false);
  const [meds, setMeds] = useState<MedicationReviewItem[]>(() =>
    patient.medications
      .filter((m) => m.isActive)
      .map((m) => ({ medicationName: m.medicationName, dose: m.dose ?? "", changeType: "continued" })),
  );
  const [planFreeText, setPlanFreeText] = useState("");
  const [nextReviewDate, setNextReviewDate] = useState<string>("");
  const [gpCopyRequested, setGpCopyRequested] = useState(false);
  const [aiAssisted, setAiAssisted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload: Record<string, unknown> = {
        consultationDate: new Date(consultationDate).toISOString(),
        consultationType,
        presentingComplaint: presentingComplaint || null,
        examinationFindings: examinationFindings || null,
        diseaseActivityTool: diseaseActivityTool || null,
        diseaseActivityScore: diseaseActivityScore ? Number(diseaseActivityScore) : null,
        medicationsReviewed: meds.filter((m) => m.medicationName.trim().length > 0),
        planFreeText: planFreeText || null,
        nextReviewDate: nextReviewDate || null,
        gpCopyRequested,
        aiAssisted,
      };
      const res = await fetch(`${BASE}/api/patients/${patient.id}/consultations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error ?? "Could not save consultation");
        return;
      }
      onCreated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-slate-900 text-lg flex items-center gap-2">
            <ClipboardPlus className="h-5 w-5 text-indigo-600" /> New consultation —{" "}
            {patient.firstName} {patient.lastName}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Consultation date</Label>
              <Input
                type="datetime-local"
                value={consultationDate}
                onChange={(e) => setConsultationDate(e.target.value)}
                required
              />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select
                value={consultationType}
                onChange={(e) => setConsultationType(e.target.value as ConsultationType)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="in-person">In person</option>
                <option value="phone">Phone</option>
                <option value="video">Video</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Presenting complaint</Label>
            <textarea
              value={presentingComplaint}
              onChange={(e) => setPresentingComplaint(e.target.value)}
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Why did the patient attend today?"
            />
          </div>

          <div>
            <Label className="text-xs">Examination findings</Label>
            <textarea
              value={examinationFindings}
              onChange={(e) => setExaminationFindings(e.target.value)}
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Joint count, ROM, systems review, observations…"
            />
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs">Disease activity tool</Label>
                <select
                  value={diseaseActivityTool}
                  onChange={(e) => setDiseaseActivityTool(e.target.value as DaScoreTool | "")}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">— None —</option>
                  <option value="das28-crp">DAS28-CRP</option>
                  <option value="das28-esr">DAS28-ESR</option>
                  <option value="cdai">CDAI</option>
                  <option value="sdai">SDAI</option>
                </select>
              </div>
              <div className="w-32">
                <Label className="text-xs">Score</Label>
                <Input
                  type="number"
                  step={0.01}
                  value={diseaseActivityScore}
                  onChange={(e) => setDiseaseActivityScore(e.target.value)}
                  disabled={!diseaseActivityTool}
                />
              </div>
              {diseaseActivityTool && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCalc((p) => !p)}
                >
                  {showCalc ? "Hide" : "Open"} calculator
                </Button>
              )}
            </div>
            {showCalc && diseaseActivityTool && (
              <ScoreCalculator
                tool={diseaseActivityTool}
                onApply={(s) => setDiseaseActivityScore(s.toString())}
              />
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs flex items-center gap-1.5">
                <Pill className="h-3.5 w-3.5 text-teal-600" /> Medication review
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  setMeds([...meds, { medicationName: "", dose: "", changeType: "new" }])
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add medication
              </Button>
            </div>
            {meds.length === 0 ? (
              <p className="text-xs text-slate-400 italic text-center py-3 border border-dashed border-slate-200 rounded-xl">
                No medications reviewed
              </p>
            ) : (
              <div className="space-y-2">
                {meds.map((m, i) => (
                  <MedReviewRow
                    key={i}
                    item={m}
                    onChange={(next) => setMeds(meds.map((x, idx) => (idx === i ? next : x)))}
                    onRemove={() => setMeds(meds.filter((_, idx) => idx !== i))}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Agreed plan</Label>
            <textarea
              value={planFreeText}
              onChange={(e) => setPlanFreeText(e.target.value)}
              rows={4}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Investigations, treatment changes, safety-netting, follow-up…"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Next review date</Label>
              <Input
                type="date"
                value={nextReviewDate}
                onChange={(e) => setNextReviewDate(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 justify-end">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={gpCopyRequested}
                  onChange={(e) => setGpCopyRequested(e.target.checked)}
                />
                Send a copy to the patient's GP
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={aiAssisted}
                  onChange={(e) => setAiAssisted(e.target.checked)}
                />
                Dictated with AI-assisted draft
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save consultation
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GpLetterModal({ recordId, onClose }: { recordId: number; onClose: () => void }) {
  const [letter, setLetter] = useState<GpLetter | null>(null);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/consultations/${recordId}/gp-letter`, {
          credentials: "include",
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          setError(b.error ?? "Could not generate letter");
          return;
        }
        const data = (await res.json()) as GpLetter;
        setLetter(data);
        setBody(data.body);
      } finally {
        setLoading(false);
      }
    })();
  }, [recordId]);

  const copy = async () => {
    await navigator.clipboard.writeText(`${letter?.subject ?? ""}\n\n${body}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(
      `<pre style="font-family: Georgia, serif; white-space: pre-wrap; padding: 32px; max-width: 720px; margin: 0 auto;">${
        letter?.subject ?? ""
      }\n\n${body.replace(/[<>&]/g, (c) =>
        c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&amp;",
      )}</pre>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-slate-900 text-lg flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" /> GP letter
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
            </div>
          ) : error ? (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          ) : letter ? (
            <>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={letter.subject} readOnly />
              </div>
              <div>
                <Label className="text-xs">Letter body — edit before sending</Label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={20}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-serif focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <p className="text-xs text-slate-500">
                Pre-populated from the consultation record. Review carefully before sending; this draft
                is not transmitted automatically.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button variant="outline" onClick={print}>
                  Print
                </Button>
                <Button onClick={copy} className="bg-amber-600 hover:bg-amber-700 text-white">
                  {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copied ? "Copied" : "Copy to clipboard"}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ConsultationCard({
  record,
  onLetter,
  viewerTeamCode,
}: {
  record: ConsultationRecord;
  onLetter: () => void;
  viewerTeamCode: string | null;
}) {
  const tool = record.diseaseActivityTool ? TOOL_LABEL[record.diseaseActivityTool] : null;
  const medChanges = record.medicationsReviewed.filter(
    (m) => m.changeType !== "continued",
  );

  return (
    <Card className="border-l-4 border-l-indigo-500">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <Stethoscope className="h-4 w-4 text-indigo-600" />
              {formatDateTime(record.consultationDate)}
              <Badge variant="outline" className="text-xs">
                {CONSULTATION_TYPE_LABEL[record.consultationType]}
              </Badge>
              {record.aiAssisted && (
                <Badge variant="outline" className="text-xs bg-violet-50 border-violet-200 text-violet-700">
                  AI-assisted
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>
                {record.clinicianName ?? "Clinician"} · {record.clinicianRole}
              </span>
              <SameTeamBadge
                viewerTeamCode={viewerTeamCode}
                otherTeamCode={record.clinicianTeamCode}
              />
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onLetter}>
            <FileText className="h-3.5 w-3.5 mr-1.5" /> GP letter
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {record.presentingComplaint && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Presenting complaint</p>
            <p className="text-slate-700 whitespace-pre-wrap">{record.presentingComplaint}</p>
          </div>
        )}
        {record.examinationFindings && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Examination</p>
            <p className="text-slate-700 whitespace-pre-wrap">{record.examinationFindings}</p>
          </div>
        )}
        {tool && record.diseaseActivityScore && (
          <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 inline-block">
            <span className="text-xs uppercase tracking-wide text-indigo-700 font-medium">{tool}</span>
            <span className="ml-2 text-slate-900 font-semibold">{record.diseaseActivityScore}</span>
          </div>
        )}
        {medChanges.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Medication changes</p>
            <ul className="text-slate-700 list-disc ml-5 mt-1">
              {medChanges.map((m, i) => (
                <li key={i}>
                  <span className="font-medium">{m.medicationName}</span>
                  {m.dose ? ` ${m.dose}` : ""}{" "}
                  <Badge variant="outline" className="text-[10px] ml-1">
                    {m.changeType}
                  </Badge>
                  {m.changeDetails ? ` — ${m.changeDetails}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {record.planFreeText && (
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Plan</p>
            <p className="text-slate-700 whitespace-pre-wrap">{record.planFreeText}</p>
          </div>
        )}
        {record.nextReviewDate && (
          <p className="text-xs text-slate-600 flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" /> Next review: {formatDate(record.nextReviewDate)}
          </p>
        )}
        {record.aiAssisted && (
          <p className="text-xs text-slate-500 italic border-t pt-2">
            Dictated with AI-assisted draft. Reviewed and confirmed by{" "}
            {record.clinicianName ?? "the clinician"} on {formatDate(record.updatedAt)}.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function PatientRecord() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const patientId = Number(params.id);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [records, setRecords] = useState<ConsultationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [showCreate, setShowCreate] = useState(false);
  const [letterRecordId, setLetterRecordId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/api/user/profile`, { credentials: "include" });
        if (res.ok) {
          const data = (await res.json()) as UserProfile;
          setProfile(data);
        }
      } finally {
        setProfileLoaded(true);
      }
    })();
  }, []);

  const reload = useMemo(
    () => async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          fetch(`${BASE}/api/med-tracker/patients/${patientId}`, { credentials: "include" }),
          fetch(`${BASE}/api/patients/${patientId}/consultations`, { credentials: "include" }),
        ]);
        if (!pRes.ok) {
          setError(pRes.status === 404 ? "Patient not found in your organisation" : "Could not load patient");
          setLoading(false);
          return;
        }
        setPatient((await pRes.json()) as PatientDetail);
        if (cRes.ok) {
          setRecords((await cRes.json()) as ConsultationRecord[]);
        }
      } finally {
        setLoading(false);
      }
    },
    [patientId],
  );

  useEffect(() => {
    if (Number.isFinite(patientId)) reload();
  }, [patientId, reload]);

  if (!profileLoaded || loading) return <LoadingScreen />;

  const role = profile?.role;
  const Layout = role === "doctor" ? DoctorLayout : NurseLayout;
  const canCreate = role === "nurse" || role === "doctor";

  const backHref = role === "doctor" ? "/med-tracker" : "/med-tracker";

  const content = (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
      <button
        onClick={() => setLocation(backHref)}
        className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="h-4 w-4" /> Back to patient list
      </button>

      {error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          {error}
        </div>
      ) : !patient ? null : (
        <>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                    <User className="h-5 w-5 text-indigo-600" />
                    {patient.firstName} {patient.lastName}
                  </h1>
                  <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>DOB {formatDate(patient.dateOfBirth)}</span>
                    <span>#{patient.hospitalNumber}</span>
                    {patient.diagnosis && <span>· {patient.diagnosis}</span>}
                  </div>
                </div>
                {canCreate && (
                  <Button
                    onClick={() => setShowCreate(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <ClipboardPlus className="h-4 w-4 mr-2" /> Record consultation
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <div>
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
              Consultation history
            </h2>
            {records.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-slate-500">
                  <Stethoscope className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm">No consultations recorded yet.</p>
                  {canCreate && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => setShowCreate(true)}
                    >
                      Record the first consultation
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {records.map((r) => (
                  <ConsultationCard
                    key={r.id}
                    record={r}
                    onLetter={() => setLetterRecordId(r.id)}
                    viewerTeamCode={profile?.teamCode ?? null}
                  />
                ))}
              </div>
            )}
          </div>

          {!canCreate && (
            <p className="text-xs text-slate-500">
              Only nurses and doctors can record new consultations. Other roles cannot see clinical
              note content.
            </p>
          )}
        </>
      )}

      {showCreate && patient && (
        <ConsultationFormModal
          patient={patient}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            reload();
          }}
        />
      )}
      {letterRecordId !== null && (
        <GpLetterModal recordId={letterRecordId} onClose={() => setLetterRecordId(null)} />
      )}
    </div>
  );

  return <Layout title="Patient record">{content}</Layout>;
}
